import type { Dayjs } from 'dayjs';

import {
  getFloodCoverageDaySetForYear,
  getFloodCoverageDaysForYear,
  yearUsesExplicitCoverageDays,
} from '@/features/map/utils/vietnamFloodScenarioCalibration';

/**
 * Tháng (1–12) cho phép khi **không** dùng danh sách ngày tường minh (fallback).
 * Union 6–11: mùa mưa–lũ điển hình (không phải mọi tháng trong năm đều có ngập).
 */
export const FLOOD_TIMELINE_DATA_MONTH_RANGE = { start: 6, end: 11 } as const;

/**
 * Cửa sổ (giờ) mà mốc thời gian vẫn hợp lệ cho kịch bản bão mô phỏng lùi từ `now`
 * — đồng bộ với bão demo (`floodDemoStormEvent`).
 */
export const FLOOD_SCRUBBER_ALLOWED_HOURS = 72 as const;

/**
 * `true` nếu `iso` nằm trong `[now - FLOOD_SCRUBBER_ALLOWED_H, now]`.
 * Chỉ dùng khi cần validate mốc scrub; không thay thế `isFloodTimelineSelectableDay` cho DatePicker lịch sử.
 */
export function isFloodScrubberAllowedIso(iso: string, nowMs: number): boolean {
  const t = Date.parse(iso);
  if (Number.isNaN(t) || !Number.isFinite(nowMs)) return false;
  const w = FLOOD_SCRUBBER_ALLOWED_HOURS * 60 * 60 * 1000;
  return t >= nowMs - w && t <= nowMs;
}

/**
 * Ngày có thể chọn cho thanh “Lịch sử 24h”:
 * - Nếu năm có `coverageDays` trong kịch bản: **chỉ** các ngày thuộc các cửa sổ đợt mưa–lũ (có khoảng nghỉ giữa các đợt).
 * - Nếu không: đúng năm và trong {@link FLOOD_TIMELINE_DATA_MONTH_RANGE}.
 */
export function isFloodTimelineSelectableDay(date: Dayjs, analysisYear: number): boolean {
  if (!date || !date.isValid()) return false;
  if (date.year() !== analysisYear) return false;

  if (yearUsesExplicitCoverageDays(analysisYear)) {
    const set = getFloodCoverageDaySetForYear(analysisYear);
    return set.has(date.format('YYYY-MM-DD'));
  }

  const month = date.month() + 1;
  return (
    month >= FLOOD_TIMELINE_DATA_MONTH_RANGE.start && month <= FLOOD_TIMELINE_DATA_MONTH_RANGE.end
  );
}

/**
 * Ngày mặc định đầu tiên hợp lệ cho năm phân tích.
 */
export function getFirstSelectableFloodTimelineDayIsoForYear(year: number): string {
  const explicit = getFloodCoverageDaysForYear(year);
  if (explicit.length > 0) {
    return explicit[0];
  }
  return `${year}-${String(FLOOD_TIMELINE_DATA_MONTH_RANGE.start).padStart(2, '0')}-01`;
}

/**
 * `true` nếu ngày nằm trong tập có phủ (khi năm dùng danh sách tường minh).
 * Dùng để tô sáng ô trên DatePicker.
 */
export function isFloodTimelineDayWithData(date: Dayjs, analysisYear: number): boolean {
  if (!date?.isValid() || date.year() !== analysisYear) return false;
  if (!yearUsesExplicitCoverageDays(analysisYear)) {
    const month = date.month() + 1;
    return (
      month >= FLOOD_TIMELINE_DATA_MONTH_RANGE.start && month <= FLOOD_TIMELINE_DATA_MONTH_RANGE.end
    );
  }
  return getFloodCoverageDaySetForYear(analysisYear).has(date.format('YYYY-MM-DD'));
}
